/**
 * PendingEmailEditor — Moteur d'edition d'un mail Juliette en attente.
 *
 * Factorise la logique (query + edit + send/reject) entre :
 *  - la route /pending-email/:id (page plein ecran historique)
 *  - le Dialog "widget" ouvert depuis BriefActions sur le dashboard
 *
 * Au lieu de gerer la navigation, ce composant signale au caller via `onDone`
 * que l'action est terminee (envoye ou annule). Le caller decide ensuite
 * (navigate back, fermer le dialog, etc.).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Eye, Loader2, Mail, Pencil, Send, Sparkles, X } from "lucide-react";
import { FunctionsHttpError } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useJulietteActiveSession } from "@/hooks/use-juliette-active-session";
import { bodyTextToHtml } from "@/lib/email-body-html";
import { StrategyNoteBanner } from "@/components/juliette/StrategyNoteBanner";

interface PendingEmailRow {
  id: string;
  team_id: string;
  contact_id: string;
  subject: string;
  body_html: string;
  body_text: string;
  status: string;
  disc_profile_used: string | null;
  grounding_flagged: boolean;
  strategy_note: string | null;
  immo_contacts: {
    first_name: string | null;
    last_name: string | null;
    name: string | null;
    email: string | null;
  } | null;
}

function displayName(c: {
  first_name?: string | null;
  last_name?: string | null;
  name?: string | null;
}): string {
  const composed = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return composed || c.name || "(sans nom)";
}

/** Extrait le vrai message d'erreur d'une FunctionsError supabase-js. */
async function extractFunctionError(err: unknown): Promise<string> {
  if (err instanceof FunctionsHttpError) {
    try {
      const body = await err.context.json();
      if (body && typeof body === "object" && typeof body.error === "string") {
        return body.error;
      }
    } catch {
      // pas de JSON parsable
    }
  }
  return err instanceof Error ? err.message : String(err);
}

export interface PendingEmailEditorProps {
  pendingId: string;
  /** Callback appele quand le mail a ete envoye ou annule. Le caller decide
   *  de la suite (navigate back, fermer un dialog, etc.). */
  onDone?: (action: "sent" | "rejected") => void;
  /** Si true, masque le header avec avatar + nom contact (utile quand le
   *  conteneur affiche deja un titre, ex Dialog). */
  hideHeader?: boolean;
}

export function PendingEmailEditor({ pendingId, onDone, hideHeader = false }: PendingEmailEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Recupere la session Systeme 1 active (si presente) pour pouvoir tracker
  // le mail envoye dans juliette_contact_actions -> le contact disparaît
  // du brief du dashboard.
  const { data: activeSession } = useJulietteActiveSession();
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState<"edit" | "preview">("edit");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // Scale CSS + hauteur scaled : on adapte l'iframe (contenu en 600px natif)
  // pour qu'il tienne entierement dans la largeur du dialog, sans scrollbars.
  const [fit, setFit] = useState<{ scale: number; height: number } | null>(null);

  const { data: pending, isLoading } = useQuery({
    queryKey: ["pending-email", pendingId],
    enabled: !!pendingId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pending_emails")
        .select(
          "id, team_id, contact_id, subject, body_html, body_text, status, " +
            "disc_profile_used, grounding_flagged, strategy_note, immo_contacts(first_name, last_name, name, email)",
        )
        .eq("id", pendingId)
        .maybeSingle();
      if (error) throw error;
      return data as PendingEmailRow | null;
    },
  });

  useEffect(() => {
    if (pending) {
      setSubject(pending.subject);
      setBodyText(pending.body_text);
    }
  }, [pending]);

  const isModified = useMemo(() => {
    if (!pending) return false;
    return subject.trim() !== pending.subject.trim() || bodyText.trim() !== pending.body_text.trim();
  }, [pending, subject, bodyText]);

  const isFinalized = pending ? ["sent", "rejected", "expired"].includes(pending.status) : false;

  const contactName = pending?.immo_contacts ? displayName(pending.immo_contacts) : "(contact inconnu)";
  const contactEmail = pending?.immo_contacts?.email ?? null;

  const loadPreview = useCallback(async () => {
    if (!pending) return;
    setPreviewLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("preview-pending-email", {
        body: {
          pending_email_id: pending.id,
          subject,
          body_text: bodyText,
        },
      });
      if (error) throw error;
      const html = (data as { data?: { html_rendered?: string } })?.data?.html_rendered;
      setPreviewHtml(html ?? null);
    } catch (err) {
      const message = await extractFunctionError(err);
      toast({
        title: "Aperçu indisponible",
        description: message,
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  }, [pending, subject, bodyText, toast]);

  // Genere la preview a chaque switch vers l'onglet "Aperçu", avec un leger
  // debounce pour ne pas spammer si l'agent toggle rapidement entre les deux.
  useEffect(() => {
    if (activeTab !== "preview" || !pending) return;
    const timer = setTimeout(() => {
      void loadPreview();
    }, 200);
    return () => clearTimeout(timer);
  }, [activeTab, pending, loadPreview]);

  // Calcul du scale pour faire rentrer l'iframe (600px natif) dans la largeur
  // du container, sans scrollbar horizontale. Re-calcule au resize de la fenetre.
  const recomputeFit = useCallback(() => {
    const iframe = iframeRef.current;
    const container = containerRef.current;
    if (!container) return;

    // Defaults : si pour une raison X on ne peut pas lire le DOM iframe
    // (ex: cross-origin avec sandbox restrictif), on prend des valeurs
    // raisonnables pour le mail Juliette (table 600 + ~700 de haut).
    let naturalWidth = 600;
    let naturalHeight = 700;
    try {
      const body = iframe?.contentDocument?.body;
      if (body) {
        naturalWidth = Math.max(body.scrollWidth, 600);
        naturalHeight = Math.max(body.scrollHeight, 200);
      }
    } catch {
      // ignore : on garde les fallbacks ci-dessus
    }

    const availableWidth = container.clientWidth;
    const scale = Math.min(1, availableWidth / naturalWidth);
    setFit({ scale, height: Math.ceil(naturalHeight * scale) });
  }, []);

  useEffect(() => {
    if (activeTab !== "preview") return;
    const handler = () => recomputeFit();
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [activeTab, recomputeFit]);

  const handleSend = useCallback(async () => {
    if (!pending) return;
    if (!contactEmail) {
      toast({
        title: "Contact sans email",
        description: "Impossible d'envoyer.",
        variant: "destructive",
      });
      return;
    }
    setSending(true);

    const newStatus = isModified ? "modified" : "approved";
    const updatePayload: Record<string, unknown> = {
      status: newStatus,
      reviewed_at: new Date().toISOString(),
      subject,
      body_text: bodyText,
      body_html: bodyTextToHtml(bodyText),
    };
    if (isModified) {
      updatePayload.modifications = {
        original_subject: pending.subject,
        original_body_text: pending.body_text,
      };
    }
    const { error: upErr } = await supabase
      .from("pending_emails")
      .update(updatePayload)
      .eq("id", pending.id);
    if (upErr) {
      setSending(false);
      toast({ title: "Sauvegarde impossible", description: upErr.message, variant: "destructive" });
      return;
    }

    const { error: sendErr } = await supabase.functions.invoke("send-pending-email", {
      body: { pending_email_id: pending.id },
    });
    setSending(false);
    if (sendErr) {
      const message = await extractFunctionError(sendErr);
      toast({
        title: "Envoi échoué",
        description: message,
        variant: "destructive",
      });
      return;
    }

    // Tracker l'action dans juliette_contact_actions pour que le contact
    // disparaisse du brief Systeme 1 du dashboard.
    try {
      await supabase.from("juliette_contact_actions" as never).insert({
        team_id: pending.team_id,
        session_id: activeSession?.id ?? null,
        contact_id: pending.contact_id,
        action_type: "replied",
        snooze_until: null,
      } as never);
    } catch {
      // Table possiblement absente (avant migration) : on continue, l'envoi
      // est deja fait.
    }

    toast({ title: `Envoyé à ${contactName}` });
    void queryClient.invalidateQueries({ queryKey: ["pending-emails"] });
    void queryClient.invalidateQueries({ queryKey: ["juliette-contact-actions"] });
    onDone?.("sent");
  }, [pending, isModified, subject, bodyText, contactEmail, contactName, activeSession, toast, queryClient, onDone]);

  const handleReject = useCallback(async () => {
    if (!pending) return;
    if (!confirm("Annuler ce mail ? Il ne sera pas envoyé.")) return;
    const { error } = await supabase
      .from("pending_emails")
      .update({ status: "rejected", reviewed_at: new Date().toISOString() })
      .eq("id", pending.id);
    if (error) {
      toast({ title: "Annulation impossible", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Mail annulé" });
    void queryClient.invalidateQueries({ queryKey: ["pending-emails"] });
    onDone?.("rejected");
  }, [pending, toast, queryClient, onDone]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Mail className="h-5 w-5 animate-pulse text-muted-foreground" />
      </div>
    );
  }

  if (!pending) {
    return (
      <div className="flex flex-col items-center justify-center py-12 gap-3 text-center">
        <p className="text-sm text-muted-foreground">Mail introuvable.</p>
        {onDone && (
          <Button variant="outline" onClick={() => onDone("rejected")}>
            Fermer
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {!hideHeader && (
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-sm shrink-0">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-base font-semibold leading-tight">{contactName}</h1>
            <p className="text-2xs text-muted-foreground leading-tight">
              {contactEmail ?? "(pas d'email)"} · {pending.status}
            </p>
          </div>
        </div>
      )}

      {pending.grounding_flagged && !isFinalized && (
        <div className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2.5">
          <AlertTriangle className="h-4 w-4 mt-0.5 text-warning shrink-0" />
          <p className="text-xs text-warning leading-snug">
            À relire attentivement : Juliette a peut-être manqué de précision sur certains points.
            Vérifie qu'aucune affirmation (biens disponibles, acheteurs…) n'est inexacte avant d'envoyer.
          </p>
        </div>
      )}

      {pending.strategy_note && (
        <StrategyNoteBanner note={pending.strategy_note} />
      )}

      <div className="space-y-1.5">
        <label htmlFor={`subject-${pendingId}`} className="text-sm font-medium block">
          Sujet
        </label>
        <Input
          id={`subject-${pendingId}`}
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          disabled={isFinalized}
          maxLength={120}
        />
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "edit" | "preview")}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="edit" className="gap-1.5">
            <Pencil className="h-3.5 w-3.5" />
            Brouillon
          </TabsTrigger>
          <TabsTrigger value="preview" className="gap-1.5">
            <Eye className="h-3.5 w-3.5" />
            Aperçu avec votre branding
          </TabsTrigger>
        </TabsList>

        <TabsContent value="edit" className="mt-3 space-y-1.5">
          <label htmlFor={`body-${pendingId}`} className="text-sm font-medium block">
            Corps du mail
          </label>
          <Textarea
            id={`body-${pendingId}`}
            value={bodyText}
            onChange={(e) => setBodyText(e.target.value)}
            disabled={isFinalized}
            rows={12}
            className="font-mono text-sm leading-relaxed"
          />
          {isModified && !isFinalized && (
            <p className="text-2xs text-warning italic">
              Tu as modifié le texte. Au prochain envoi, il sera marqué "modifié" pour traçabilité.
            </p>
          )}
        </TabsContent>

        <TabsContent value="preview" className="mt-3">
          <div
            ref={containerRef}
            className="rounded-md border border-border overflow-hidden bg-white relative"
            style={{ height: fit ? fit.height : 460 }}
          >
            {previewLoading ? (
              <div className="absolute inset-0 flex items-center justify-center bg-white/90">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Génération du rendu…</span>
              </div>
            ) : previewHtml ? (
              <iframe
                ref={iframeRef}
                title="Aperçu du mail"
                // allow-same-origin requis pour lire scrollWidth/scrollHeight du body
                // depuis le parent (recomputeFit). Pas de allow-scripts : aucun JS
                // execute dans la preview (securite).
                sandbox="allow-same-origin"
                srcDoc={previewHtml}
                onLoad={recomputeFit}
                style={{
                  width: 600,
                  height: fit ? Math.ceil(fit.height / fit.scale) : 600,
                  border: 0,
                  transform: fit ? `scale(${fit.scale})` : undefined,
                  transformOrigin: "top left",
                  display: "block",
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
                Aperçu indisponible
              </div>
            )}
          </div>
          <p className="text-2xs text-muted-foreground mt-2 italic">
            Voilà ce que verra {contactName.split(" ")[0]} dans sa boîte mail (logo, signature et footer de ton agence).
          </p>
        </TabsContent>
      </Tabs>

      {!isFinalized && (
        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2 border-t border-border">
          <Button variant="outline" onClick={() => void handleReject()} disabled={sending}>
            <X className="h-4 w-4 mr-1.5" />
            Annuler
          </Button>
          <Button
            onClick={() => void handleSend()}
            disabled={sending || !contactEmail || !subject.trim() || !bodyText.trim()}
          >
            {sending ? <Mail className="h-4 w-4 mr-1.5 animate-pulse" /> : <Send className="h-4 w-4 mr-1.5" />}
            {sending ? "Envoi…" : isModified ? "Modifier & envoyer" : "Valider & envoyer"}
          </Button>
        </div>
      )}

      {isFinalized && (
        <div className="rounded-lg border border-border bg-card/50 px-4 py-3 text-center text-sm text-muted-foreground">
          Mail {pending.status === "sent" ? "envoyé" : pending.status === "rejected" ? "annulé" : "expiré"} —
          plus d'action possible.
        </div>
      )}
    </div>
  );
}
